import React, { createContext, useContext, useEffect, useState, useMemo, useRef }  from 'react';
import { message, Modal, Input, Button, Form } from 'antd';
import { UnlockFilled, RollbackOutlined } from '@ant-design/icons';
import { GoogleLogin } from '@react-oauth/google';
import { useHistory } from 'react-router-dom';
import { useLocalStorage } from '../hooks/useLocalStorage';
import dayjs from 'dayjs';
import axios from "../api/AxiosInstance";
import jwtDecode from 'jwt-decode';
import { useAppConfig } from '../hooks/useAppConfig';

const { useForm } = Form;

export const AuthContext = createContext({});

export const useAuthenticatedSession = () => {
  const {
    isLocked,
    user,
  } = useContext(AuthContext);

  return {
    isLocked, 
    user,
  }
}

export const useAuthentication = () => {
  const [refreshToken, setRefreshToken, removeRefreshToken, refreshTokenRef] = useLocalStorage(
    'refreshToken',
    { value: null, expiresAt: null, locksAt: null },
  );

  const [accessToken, setAccessToken, removeAccessToken] = useLocalStorage(
    'userToken',
    '',
    {
      serializer: String,
      parser: String
    }
  );

  const history = useHistory();

  const setLegacyItems = (token) => {
    const { user } = jwtDecode(token);
    localStorage.setItem('userEmail', user.email);
    localStorage.setItem('type', user.type);
  }

  const login = async ({ email, password }) => {
    try {
      if (!email) throw new Error('Email is required');
      if (!password) throw new Error('Password is required');

      const { data, status } = await axios.post('/auth/login', {
        email: email.toString().toLowerCase(),
        password
      });

      if (status === 200) {
        setRefreshToken({
          value: data.payload.refreshToken,
          expiresAt: data.payload.sessionExpiresAt,
          locksAt: data.payload.sessionLocksAt,
          authMethod: data.payload.authMethod,
        });

        setAccessToken(data.payload.accessToken);
        setLegacyItems(data.payload.accessToken);

        history.push(`/`);
      }
      else if (status === 202) {
        const userId = data.payload;
        history.push(`/verify-otp/${userId}`);
      }

      message.success(data.message);
    }
    catch (err) {
      message.error(err.response?.data.message || err.message);
      throw err;
    }
  }


  const resendOTP = async ({ userId }) => {
    try {
      if (!userId) throw new Error('User ID is required');

      const { data } = await axios.post('/auth/resend-otp', { userId });

      message.success(data.message);
    }
    catch (err) {
      message.error(err.response?.data.message || err.message);

      const isSuspended = err.response?.status === 403;
      const isExpired = err.response?.status === 409;

      if (isExpired || isSuspended) {
        history.push('/login');
      }
      else {
        throw err;
      }
    }
  }

  const subscribeOTP = async ({ userId }) => {
    try {
      if (!userId) throw new Error('User ID is required');

      const { data } = await axios.post('/auth/subscribe-otp', { userId });

      message.success(data.message);
    }
    catch (err) {
      message.error(err.response?.data.message || err.message);
    }
  }

  const verifyOTP = async ({ userId, otp })  => {
    try {
      if (!otp) throw new Error('OTP is required');
      
      const { data } = await axios.post('/auth/otp', {
        userId,
        otp: otp.toString(),
      });

      setRefreshToken({
        value: data.payload.refreshToken,
        expiresAt: data.payload.sessionExpiresAt,
        locksAt: data.payload.sessionLocksAt,
        authMethod: data.payload.authMethod,
      })

      setAccessToken(data.payload.accessToken);
      setLegacyItems(data.payload.accessToken);

      history.push('/');

      message.success(data.message);
    }
    catch (err) {
      message.error(err.response?.data.message || err.message);

      const isSuspended = err.response?.status === 403;
      const isExpired = err.response?.status === 409;

      if (isExpired || isSuspended) {
        history.push('/login');
      }
      else {
        throw err;
      }
    }
  }
  
  const logout = async (options={ all: false }) => {
    console.info('clearing access token and logging out...');

    if (options.all) {
      axios.post('/auth/logout/all').catch(err => console.info(err));
    }
    else {
      axios.post('/auth/logout', { refreshToken: refreshTokenRef.current.value }).catch(err => console.info(err));
    }

    removeAccessToken();
    removeRefreshToken();

    history.push('/login');
  }

  const googleLogin = async (credential, selectedUserId) => {
    try {
      if (!credential) throw new Error('Google credential is required');

      const { data, status } = await axios.post('/auth/google', { credential, selectedUserId });

      if (status === 202) {
        return { candidates: data.payload.candidates };
      }

      setRefreshToken({
        value: data.payload.refreshToken,
        expiresAt: data.payload.sessionExpiresAt,
        locksAt: data.payload.sessionLocksAt,
        authMethod: data.payload.authMethod,
      });

      setAccessToken(data.payload.accessToken);
      setLegacyItems(data.payload.accessToken);

      history.push('/');

      message.success(data.message);
    } catch (err) {
      message.error(err.response?.data?.message || err.message);
      throw err;
    }
  }

  const forgotPassword = async (email) => {
    return axios.post('/auth/forgot-password', { email: email.toString().toLowerCase() });
  }

  const exchangeRefreshToken = async (password, credential) => {
    try {
      const payload = {
        refreshToken: refreshTokenRef.current.value,
      };

      if (typeof password === 'string' && password.trim()) payload.password = password;
      if (typeof credential === 'string' && credential.trim()) payload.credential = credential;

      const { data } = await axios.post('/auth/token/refresh', payload);

      setRefreshToken({
        value: data.payload.refreshToken,
        expiresAt: data.payload.sessionExpiresAt,
        locksAt: data.payload.sessionLocksAt,
        authMethod: data.payload.authMethod || refreshTokenRef.current.authMethod,
      });

      setAccessToken(data.payload.accessToken);
      setLegacyItems(data.payload.accessToken);
    }
    catch (err) {
      if (err.response?.status === 402) {
        setRefreshToken({
          ...refreshTokenRef.current,
          locksAt: null
        });
        return;
      }
      else {
        message.warning(err.response?.data?.message || err.message);
        throw err;
      }
    }
  }


  return {
    accessToken,
    refreshToken,
    refreshTokenRef,
    exchangeRefreshToken,
    login,
    googleLogin,
    logout,
    verifyOTP,
    resendOTP,
    subscribeOTP,
    forgotPassword
  }
}

export const AuthenticatedSessionProvider = ({ children }) => {
  const { accessToken, refreshToken, refreshTokenRef, exchangeRefreshToken, logout } = useAuthentication();
  const [isLocked, setIsLocked] = useState(!refreshTokenRef.current.locksAt || new Date() > new Date(refreshTokenRef.current.locksAt));

  const user = useMemo(() => 
    !!accessToken
      ? jwtDecode(accessToken).user 
      : null
  , [accessToken]);


  const USER_SESSION_LOCK_MINUTES = useAppConfig('USER_SESSION_LOCK_MINUTES');

  const lastActiveAtRef = useRef(new Date());
  const isLockedRef = useRef(isLocked);
  const isIdleRef = useRef(false);
  const autoRefreshIntervalRef = useRef(null);
  const checkIsIdleIntervalRef = useRef(null);

  const checkSessionState = () => {
      const currentDateTime = new Date();
      const expiresAt = refreshTokenRef.current.expiresAt ? new Date(refreshTokenRef.current.expiresAt) : null;
      const locksAt = refreshTokenRef.current.locksAt ? new Date(refreshTokenRef.current.locksAt) : null;

      const tokenExpired = !expiresAt || currentDateTime > expiresAt;
      const tokenLocked = !locksAt || currentDateTime > locksAt;

      if (tokenExpired) {
        console.info('session token expired, logging out', { expiresAt });
        return logout();
      }
      else if (tokenLocked && !isLockedRef.current) {
        console.info('session unlock key expired, locking session', { locksAt });
        return setIsLocked(true);
      }
      else if (!tokenLocked && isLockedRef.current) {
        console.info('session unlock key renewed, unlocking session');
        return setIsLocked(false);
      }
  }

  useEffect(() => {
    if (!refreshToken.value) { 
      logout();
    }
  }, [refreshToken])

  useEffect(() => {
    if (accessToken) {
      const decoded = jwtDecode(accessToken);
      if (Date.now() > decoded.exp * 1000) {
        exchangeRefreshToken();
      }
    }
  }, [accessToken])

  useEffect(() => {
    isLockedRef.current = isLocked;

    if (!isLocked) {
      const handleActivity = () => {
        lastActiveAtRef.current = new Date();
        isIdleRef.current = false;
      };


      addEventListener('click', handleActivity);
      addEventListener('mousemove', handleActivity);
      addEventListener('keydown', handleActivity);

      return () => {
        removeEventListener('click', handleActivity);
        removeEventListener('mousemove', handleActivity);
        removeEventListener('keydown', handleActivity);
      };
    }
  }, [isLocked]);

  useEffect(() => {
    const checkSessionStateInterval = setInterval(checkSessionState, 1000);

    return () => {
      clearInterval(checkSessionStateInterval);
    }
  }, [])

  useEffect(() => {
    if (USER_SESSION_LOCK_MINUTES) {
      const REFRESH_INTERVAL_MILLISECONDS = (1000 * 60 * USER_SESSION_LOCK_MINUTES) / 2;

      const createAutoRefreshInterval = () => {
        if (!isLockedRef.current && !autoRefreshIntervalRef.current) {
          exchangeRefreshToken();

          console.info('starting auto refresh');
          autoRefreshIntervalRef.current = setInterval(exchangeRefreshToken, REFRESH_INTERVAL_MILLISECONDS);
        }
      }

      createAutoRefreshInterval();

      checkIsIdleIntervalRef.current = setInterval(() => {
        if (isIdleRef.current) return;

        const currentDateTime = new Date();
        const lastActiveExpiresAt = dayjs(lastActiveAtRef.current).add(REFRESH_INTERVAL_MILLISECONDS, 'millisecond').toDate();

        const isIdle = currentDateTime > lastActiveExpiresAt;
        isIdleRef.current = isIdle;

        if (!isIdle) return createAutoRefreshInterval();

        console.info('idle state detected, auto refresh paused');
        clearInterval(autoRefreshIntervalRef.current);
        autoRefreshIntervalRef.current = null;
      }, 1000);

      return () => {
        clearInterval(checkIsIdleIntervalRef.current);
        clearInterval(autoRefreshIntervalRef.current);
        checkIsIdleIntervalRef.current = null;
        autoRefreshIntervalRef.current = null;
      }
    }
  }, [USER_SESSION_LOCK_MINUTES])

  return (
    <AuthContext.Provider value={{ 
      isLocked, 
      user,
    }}>
      {children}
      <AuthenticatedSessionLock />
    </AuthContext.Provider>
  )
}

const AuthenticatedSessionLock = () => {
  const [form] = useForm();
  const { refreshToken, refreshTokenRef, exchangeRefreshToken, logout } = useAuthentication();
  const { isLocked } = useAuthenticatedSession();

  if (!refreshToken) return null

  const isGoogleSession = refreshTokenRef.current.authMethod === 'google';

  return (
      <Modal
        open={isLocked}
        maskClosable={false}
        closable={false}
        title='Session Locked Due To Inactivity'
        centered={true}
        footer={isGoogleSession
          ? <Button icon={<RollbackOutlined />} type='default' onClick={logout}>Log Out</Button>
          : <>
              <Button icon={<RollbackOutlined />} type='default' onClick={logout}>Log Out</Button>
              <Button icon={<UnlockFilled />} type='primary' onClick={form.submit}>Unlock</Button>
            </>
        }
      >
        {isGoogleSession ? (
          <div style={{ textAlign: 'center' }}>
            <p style={{ marginBottom: 16 }}>Sign in with Google below to unlock your session.</p>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <GoogleLogin
                onSuccess={(credentialResponse) => {
                  exchangeRefreshToken(null, credentialResponse.credential);
                }}
                onError={() => {
                  message.error('Google sign-in failed');
                }}
                text="signin_with"
                shape="rectangular"
                size="large"
              />
            </div>
          </div>
        ) : (
          <Form form={form} onFinish={(values) => {
            exchangeRefreshToken(values.password)
              .finally(form.resetFields)
          }}>
            <Form.Item name='password' label='Password' rules={[{ required: true, message: 'Password is required' }]}>
              <Input.Password />
            </Form.Item>
          </Form>
        )}
      </Modal>
  )
}